import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Tabs, Tab, Card, CardContent, Grid,
    Button, TextField, InputAdornment,Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, IconButton,
    Divider, Chip
} from '@mui/material';
import {
    Copy, Search, FileText, MessageSquare, Mail,
    DollarSign, CheckCircle2, Plus, Pencil, Trash2,
    Star, Eye
} from 'lucide-react';
import axios from 'axios';

const SalesToolkit = () => {
    // 1. Core State Handlers
    const [templates, setTemplates] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Feature Extension: Favorites & Usage Counters
    const [favorites, setFavorites] = useState(() => {
        const saved = localStorage.getItem('pinned_templates');
        return saved ? JSON.parse(saved) : [];
    });

    // Feature Extension: Preview & Dynamic Parsing Modal
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [variableInputs, setVariableInputs] = useState({});

    // 2. State for Manager Management Modal (Admin/HR Only)
    const [openModal, setOpenModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [newTemplate, setNewTemplate] = useState({
        category: 'whatsapp',
        title: '',
        description: '',
        body: ''
    });

    // 3. User Validation Profile Inferences
    const rawRole = localStorage.getItem('user_role') || '';
    const roleLower = String(rawRole).trim().toLowerCase();
    const isManagement = roleLower === 'admin' || roleLower === 'hr';

    // 4. Read: Fetch Templates Database Hooks
    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/templates`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.data.success) {
                setTemplates(response.data.data);
            }
        } catch (error) {
            console.error('Error hitting template endpoint extraction:', error);
            setSnackbar({ open: true, message: 'Failed to synchronize matching toolkit tables.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    // 5. Write: Submit Create / Update Data Payload
    const handleAddTemplateSubmit = async () => {
        if (!newTemplate.title.trim() || !newTemplate.body.trim()) {
            setSnackbar({ open: true, message: 'Please complete title and script body criteria.', severity: 'warning' });
            return;
        }

        try {
            let response;
            if (editingId) {
                response = await axios.put(`${process.env.REACT_APP_API_URL}/templates/${editingId}`, newTemplate, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
            } else {
                response = await axios.post(`${process.env.REACT_APP_API_URL}/templates`, newTemplate, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
            }

            if (response.data.success) {
                setSnackbar({
                    open: true,
                    message: editingId ? 'Template updated successfully!' : 'New sales template inserted successfully!',
                    severity: 'success'
                });
                handleCloseModal();
                fetchTemplates();
            }
        } catch (error) {
            console.error('Network/Database Layer Exception:', error);
            const backendErrorMessage = error.response?.data?.message || 'Failed to persist template onto tables.';
            setSnackbar({ open: true, message: backendErrorMessage, severity: 'error' });
        }
    };

    // 6. Delete: Remove Template Row Handler
    const handleDeleteTemplate = async (id) => {
        if (!window.confirm('Are you absolute sure you want to delete this template layout element?')) return;

        try {
            const response = await axios.delete(`${process.env.REACT_APP_API_URL}/templates/${id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.data.success) {
                setSnackbar({ open: true, message: 'Template removed from server tables successfully.', severity: 'success' });
                fetchTemplates();
            }
        } catch (error) {
            console.error('Deletion operation exception:', error);
            const backendErrorMessage = error.response?.data?.message || 'Failed to delete data target row.';
            setSnackbar({ open: true, message: backendErrorMessage, severity: 'error' });
        }
    };

    // Feature Extensions Logic Handlers
    const toggleFavorite = (id) => {
        const updated = favorites.includes(id)
            ? favorites.filter(favId => favId !== id)
            : [...favorites, id];
        setFavorites(updated);
        localStorage.setItem('pinned_templates', JSON.stringify(updated));
        setSnackbar({ open: true, message: 'Preference updated successfully.', severity: 'success' });
    };

    const handleCopy = async (template, customBody = null) => {
        const textToCopy = customBody || template.body;
        navigator.clipboard.writeText(textToCopy);
        setSnackbar({ open: true, message: 'Copied to clipboard safely!', severity: 'success' });

        // Analytics Analytics Tracker Feature: Update state locally and send count patch request 
        try {
            await axios.patch(`${process.env.REACT_APP_API_URL}/templates/${template.id || template._id}/increment-usage`, {}, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            // Update counts in current UI layout seamlessly
            setTemplates(prev => prev.map(t => (t.id === template.id || t._id === template._id) ? { ...t, usageCount: (t.usageCount || 0) + 1 } : t));
        } catch (err) {
            console.log('Analytics tracking non-blocking mismatch');
        }
    };

    const handleOpenPreviewModal = (template) => {
        setPreviewTemplate(template);
        // Find match components matching brackets {example}
        const matches = template.body.match(/\{.*?\}/g) || [];
        const uniqueVars = [...new Set(matches)].reduce((acc, current) => {
            acc[current] = '';
            return acc;
        }, {});
        setVariableInputs(uniqueVars);
    };

    const getParsedBody = () => {
        if (!previewTemplate) return '';
        let parsed = previewTemplate.body;
        Object.keys(variableInputs).forEach(key => {
            const replacement = variableInputs[key] || key; // Falls back to default wrapper if empty
            parsed = parsed.replaceAll(key, replacement);
        });
        return parsed;
    };

    const handleOpenEditModal = (template) => {
        setEditingId(template.id || template._id);
        setNewTemplate({
            category: template.category,
            title: template.title,
            description: template.description || '',
            body: template.body
        });
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setEditingId(null);
        setNewTemplate({ category: 'whatsapp', title: '', description: '', body: '' });
    };

    const categories = ['whatsapp', 'email', 'pricing'];
    const activeCategory = categories[activeTab];

    // Sort templates layout: favorites float to the front index sorting array dynamically
    const filteredTemplates = templates
        .filter(template => {
            const matchesCategory = template.category === activeCategory;
            const matchesSearch = template.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                template.body?.toLowerCase().includes(searchQuery.toLowerCase());
            return matchesCategory && matchesSearch;
        })
        .sort((a, b) => {
            const aId = a.id || a._id;
            const bId = b.id || b._id;
            return (favorites.includes(bId) ? 1 : 0) - (favorites.includes(aId) ? 1 : 0);
        });

    return (
        <Box sx={{ bgcolor: '#f3f3f3', height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: '"Salesforce Sans", Arial, sans-serif', overflow: 'hidden' }}>

            {/* Salesforce Utility Setup Sticky Header */}
            <Box sx={{ 
                position: 'sticky', 
                top: 0, 
                zIndex: 1100, 
                bgcolor: '#1b5eae', 
                color: '#ffffff', 
                px: 3, 
                py: 1.5, 
                height: '29px',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                borderBottom: '3px solid #0070d2',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ bgcolor: '#52b788', p: 0.8, borderRadius: '4px', display: 'flex', alignItems: 'center' }}>
                        <FileText size={18} color="#fff" />
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.8, textTransform: 'uppercase', fontSize: '10px', fontWeight: 700, letterSpacing: '1px' }}>
                            Sales Cloud Console
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px', lineHeight: 1.2 }}>
                            Sales Enablement Hub
                        </Typography>
                    </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {isManagement && (
                        <Button
                            variant="contained"
                            startIcon={<Plus size={16} />}
                            onClick={() => setOpenModal(true)}
                            sx={{
                                bgcolor: '#0070d2', 
                                textTransform: 'none', 
                                borderRadius: '4px', 
                                fontWeight: 600, 
                                fontSize: '13px', 
                                px: 2,
                                border: '1px solid #005fb2',
                                '&:hover': { bgcolor: '#005fb2' }, 
                                boxShadow: 'none'
                            }}
                        >
                            New Template
                        </Button>
                    )}
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>Internal Repository</Typography>
                </Box>
            </Box>

            {/* Salesforce Workspace Framework Action Board - Content Scroller Only */}
            <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', gap: 3, overflowY: 'auto' }}>

                {/* Scope Filters & Search Controls Segment */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { md: 'center' }, gap: 2, bgcolor: '#ffffff', border: '1px solid #dddbda', borderRadius: '4px', p: 1.5 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{
                            minHeight: '36px',
                            '& .MuiTabs-indicator': { bgcolor: '#0070d2', height: 3 },
                            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '13px', minWidth: 120, minHeight: '36px', color: '#3e3e3c', py: 1 },
                            '& .Mui-selected': { color: '#0070d2 !important' }
                        }}
                    >
                        <Tab icon={<MessageSquare size={14} />} iconPosition="start" label="WhatsApp Logs" />
                        <Tab icon={<Mail size={14} />} iconPosition="start" label="Email Shells" />
                        <Tab icon={<DollarSign size={14} />} iconPosition="start" label="Pricing Matrices" />
                    </Tabs>

                    <TextField
                        placeholder="Search workspace index..."
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ color: '#706e6b' }}>
                                    <Search size={14} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: { xs: '100%', md: 300 },
                            '& .MuiOutlinedInput-root': { borderRadius: '4px', height: '34px', fontSize: '13px', bgcolor: '#f3f3f3' }
                        }}
                    />
                </Box>

                {/* Templates Grid Render Interface */}
                {loading ? (
                    <Box sx={{ py: 8, textAlign: 'center', color: '#706e6b', bgcolor: '#ffffff', border: '1px solid #dddbda', borderRadius: '4px' }}>
                        Refreshing layout tables context pipelines...
                    </Box>
                ) : filteredTemplates.length > 0 ? (
                    <Grid container spacing={2}>
                        {filteredTemplates.map((template) => {
                            const templateId = template.id || template._id;
                            const isPinned = favorites.includes(templateId);

                            return (
                                <Grid item xs={12} key={templateId}>
                                    <Card sx={{ borderRadius: '4px', border: isPinned ? '1px solid #1589ee' : '1px solid #dddbda', boxShadow: 'none', position: 'relative', '&:hover': { boxShadow: '0 2px 4px rgba(0,0,0,0.08)' } }}>
                                        {isPinned && <Box sx={{ width: '4px', bgcolor: '#0070d2', position: 'absolute', top: 0, bottom: 0, left: 0 }} />}
                                        <CardContent sx={{ p: 2, pl: isPinned ? 2.5 : 2, '&:last-child': { pb: 2 } }}>

                                            {/* Top Metadata Header Context */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <IconButton size="small" onClick={() => toggleFavorite(templateId)} sx={{ color: isPinned ? '#f39c12' : '#dddbda', p: 0.5 }}>
                                                        <Star size={16} fill={isPinned ? '#f39c12' : 'transparent'} />
                                                    </IconButton>
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#081c3b', fontSize: '14px' }}>
                                                            {template.title}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#514f4d', display: 'block', fontSize: '12px' }}>
                                                            {template.description || 'No instruction manual description specified.'}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Salesforce Standard Button Panel Context Strip */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Chip label={`Used: ${template.usageCount || 0}x`} size="small" variant="outlined" sx={{ borderRadius: '4px', fontSize: '11px', height: '22px', color: '#444', border: '1px solid #dddbda', mr: 1 }} />

                                                    {isManagement && (
                                                        <>
                                                            <IconButton size="small" onClick={() => handleOpenEditModal(template)} sx={{ color: '#0070d2', borderRadius: '4px', p: 0.5 }}>
                                                                <Pencil size={14} />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => handleDeleteTemplate(templateId)} sx={{ color: '#c23934', borderRadius: '4px', p: 0.5 }}>
                                                                <Trash2 size={14} />
                                                            </IconButton>
                                                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
                                                        </>
                                                    )}

                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handleOpenPreviewModal(template)}
                                                        startIcon={<Eye size={12} />}
                                                        sx={{ textTransform: 'none', borderRadius: '4px', color: '#0070d2', borderColor: '#dddbda', fontSize: '12px', height: '28px', '&:hover': { bgcolor: '#f4f6f9', borderColor: '#0070d2' } }}
                                                    >
                                                        Preview
                                                    </Button>

                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => handleCopy(template)}
                                                        startIcon={<Copy size={12} />}
                                                        sx={{ textTransform: 'none', borderRadius: '4px', bgcolor: '#ffffff', color: '#0070d2', border: '1px solid #dddbda', fontSize: '12px', height: '28px', boxShadow: 'none', '&:hover': { bgcolor: '#f4f6f9' } }}
                                                    >
                                                        Copy Base
                                                    </Button>
                                                </Box>
                                            </Box>

                                            {/* Code Editor Body Grid Frame */}
                                            <Box sx={{ bgcolor: '#f3f2f1', p: 1.5, borderRadius: '4px', border: '1px solid #ecebea', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px', color: '#2b2826', mt: 1.5 }}>
                                                {template.body?.split(/(\{.*?\})/).map((chunk, i) => (
                                                    chunk.startsWith('{') && chunk.endsWith('}') ? (
                                                        <Box component="span" key={i} sx={{ bgcolor: '#ffe8e8', color: '#c23934', fontWeight: 600, px: 0.5, py: 0.1, borderRadius: '2px', border: '1px solid #f9d6d6' }}>
                                                            {chunk}
                                                        </Box>
                                                    ) : chunk
                                                ))}
                                            </Box>

                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                ) : (
                    <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#ffffff', border: '1px dashed #dddbda', borderRadius: '4px' }}>
                        <FileText size={32} color="#706e6b" style={{ marginBottom: '8px' }} />
                        <Typography variant="subtitle2" sx={{ color: '#3e3e3c' }}>No matching items located inside categories array.</Typography>
                    </Box>
                )}
            </Box>

            {/* Feature Modal Extension: Variable Interpolator & Injector Form Preview */}
            <Dialog open={!!previewTemplate} onClose={() => setPreviewTemplate(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
                <DialogTitle sx={{ fontWeight: 700, color: '#081c3b', fontSize: '16px', borderBottom: '1px solid #dddbda', py: 2 }}>
                    Salesforce Live Variable Parser
                </DialogTitle>
                <DialogContent sx={{ py: 2.5 }}>
                    {previewTemplate && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>
                                ENTER VALUE DATA FOR INTERACTION VARIABLES:
                            </Typography>

                            {Object.keys(variableInputs).length === 0 ? (
                                <Typography variant="body2" sx={{ color: '#706e6b', italic: true }}>No dynamic merge tags detected inside template structure strings.</Typography>
                            ) : (
                                <Grid container spacing={2}>
                                    {Object.keys(variableInputs).map((variable) => (
                                        <Grid item xs={12} sm={6} key={variable}>
                                            <TextField
                                                label={variable}
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                value={variableInputs[variable]}
                                                onChange={(e) => setVariableInputs({ ...variableInputs, [variable]: e.target.value })}
                                                InputLabelProps={{ style: { fontSize: '12px' } }}
                                                inputProps={{ style: { fontSize: '13px', padding: '8px' } }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            )}

                            <Divider sx={{ my: 1 }} />

                            <Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Parsed Live Preview Container Script:</Typography>
                            <Box sx={{ bgcolor: '#081c3b', color: '#ffffff', p: 2, borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px', whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto' }}>
                                {getParsedBody()}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #dddbda', bgcolor: '#f3f2f1' }}>
                    <Button onClick={() => setPreviewTemplate(null)} sx={{ color: '#0070d2', textTransform: 'none', fontSize: '13px', fontWeight: 600 }}>Close Preview</Button>
                    <Button
                        variant="contained"
                        onClick={() => { handleCopy(previewTemplate, getParsedBody()); setPreviewTemplate(null); }}
                        sx={{ bgcolor: '#0070d2', textTransform: 'none', borderRadius: '4px', fontSize: '13px', '&:hover': { bgcolor: '#005fb2' } }}
                    >
                        Copy Filled Content
                    </Button>
                </DialogActions>
            </Dialog>

            {/* CRUDS Management Dialog Form Framework */}
            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
                <DialogTitle sx={{ fontWeight: 700, color: '#081c3b', fontSize: '16px', borderBottom: '1px solid #dddbda' }}>
                    {editingId ? "Modify Toolkit Template Object" : "Create Toolkit Data Row"}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            select label="Target Channel Category" fullWidth size="small"
                            value={newTemplate.category}
                            onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                        >
                            <MenuItem value="whatsapp">WhatsApp Script Log</MenuItem>
                            <MenuItem value="email">Email System Layout</MenuItem>
                            <MenuItem value="pricing">Pricing Matrix Table</MenuItem>
                        </TextField>
                        <TextField
                            label="Template Context Title" fullWidth size="small" placeholder="e.g., Trial End Closing Pitch"
                            value={newTemplate.title}
                            onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })}
                        />
                        <TextField
                            label="Description Context Guidelines" fullWidth size="small" placeholder="e.g., Run this pipeline script manually when leads disconnect"
                            value={newTemplate.description}
                            onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                        />
                        <TextField
                            label="Message/Script Body Content" fullWidth size="small" multiline rows={5}
                            placeholder="Build text content layouts here. Use tracking braces like {Prospect Name} to trigger user dynamic fields parameters."
                            value={newTemplate.body}
                            onChange={(e) => setNewTemplate({ ...newTemplate, body: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #dddbda', bgcolor: '#f3f2f1' }}>
                    <Button onClick={handleCloseModal} sx={{ color: '#444', textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button onClick={handleAddTemplateSubmit} variant="contained" sx={{ bgcolor: '#0070d2', textTransform: 'none', borderRadius: '4px', '&:hover': { bgcolor: '#005fb2' } }}>
                        {editingId ? "Save Edits Pipeline" : "Insert Component Log"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar Component */}
            <Snackbar
                open={snackbar.open} autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} icon={<CheckCircle2 size={16} />} sx={{ borderRadius: '4px', fontWeight: 600, bgcolor: '#04844b', color: '#fff', '& .MuiAlert-icon': { color: '#fff' } }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SalesToolkit;